import { useContext, useState } from 'react';
import { useCallback } from 'react';
import { HimmaContext } from '../context/HimmaContext';
import {
  AddOfferRequestSchema,
  EditOfferRequestSchema,
  DeleteOffersRequestSchema,
  ToggleOfferStatusRequestSchema,
  AssignOfferRequestSchema,
  UpdateWalletRequest,
} from '../client-sdk/api';

export const useOffersApi = () => {
  const api = useContext(HimmaContext)!;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Create Offer
  const createOffer = useCallback(
    async (offerData: AddOfferRequestSchema) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.addOffer(offerData);
        if (response?.data) {
          return response.data;
        }
        throw new Error('Failed to create offer: No response data');
      } catch (err: any) {
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          'An error occurred while creating offer';
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  // Edit Offer
  const editOffer = useCallback(
    async (offerId: string, offerData: EditOfferRequestSchema) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.editOffer(offerId, offerData);
        // Return response.data if it exists, otherwise return the full response
        // This handles cases where the response structure might vary
        return response?.data || response || { message: 'Offer updated successfully' };
      } catch (err: any) {
        // Only throw error if it's an actual API error (not a successful response)
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          'An error occurred while updating offer';
        setError(errorMessage);
        throw err; // Re-throw the original error to preserve error structure
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  // List Offers
  const getAllOffers = useCallback(
    async (query: any) => {
      setLoading(true);
      setError(null);
      try {
        // Only include search parameter if it has a value
        const searchParam =
          query.search && query.search.trim() !== '' ? query.search : undefined;
        const response = await api.getAllOffers(
          query.page,
          query.limit,
          searchParam,
          query.sortBy,
          query.sortOrder
        );
        return response.data;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  // Delete Offers
  const deleteOffers = useCallback(
    async (offerIds: string[]) => {
      setLoading(true);
      setError(null);
      try {
        const deleteOffersRequest: DeleteOffersRequestSchema = {
          offerIds: offerIds,
        };
        const response = await api.deleteOffers(deleteOffersRequest);
        return response.data;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  // Block/Unblock Offers
  const toggleOfferStatus = useCallback(
    async (offerIds: string[], status: 'blocked' | 'unblocked') => {
      setLoading(true);
      setError(null);
      try {
        const toggleStatusRequest: ToggleOfferStatusRequestSchema = {
          offerIds: offerIds,
          status: status,
        };
        const response = await api.toggleOfferStatus(toggleStatusRequest);
        return response.data;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  // Generate Coupon Code
  const generateCouponCode = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.generateCouponCode();
      return response.data;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [api]);

  // Assign Offer
  const assignOffer = useCallback(
    async (assignData: AssignOfferRequestSchema) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.assignOffer(assignData);
        if (response?.data) {
          return response.data;
        }
        throw new Error('Failed to assign offer: No response data');
      } catch (err: any) {
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          'An error occurred while assigning offer';
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  // Get Available Offers for Assignment
  const getAvailableOffersForAssignment = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.getAvailableOffersForAssignment();
      return response.data;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [api]);

  // Get Customer Details for Offers
  const getCustomerDetails = useCallback(
    async (userId: string) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.getCustomerDetails(userId);
        return response.data;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  // Get Customer Loyalty History
  const getCustomerLoyaltyHistory = useCallback(
    async (userId: string, page: number, limit: number) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.getCustomerLoyaltyHistory(userId, page, limit);
        return response.data;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  // Get Customers Details for Offers (list)
  const getCustomersDetailsForOffers = useCallback(
    async (query: any) => {
      setLoading(true);
      setError(null);
      try {
        // URL encode the search parameter to handle spaces and special characters
        const searchParam =
          query.search && query.search.trim() !== '' 
            ? encodeURIComponent(query.search.trim()) 
            : undefined;
        const response = await api.getCustomersDetailsForOffers(
          query.page,
          query.limit,
          searchParam,
          query.sortBy,
          query.sortOrder,
          query.status,
          query.customerType
        );
        return response.data;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  // Update Wallet
  const updateWallet = useCallback(
    async (walletData: UpdateWalletRequest) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.updateWallet(walletData);
        if (response?.data) {
          return response.data;
        }
        throw new Error('Failed to update wallet: No response data');
      } catch (err: any) {
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          'An error occurred while updating wallet';
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  return {
    createOffer,
    editOffer,
    getAllOffers,
    deleteOffers,
    toggleOfferStatus,
    generateCouponCode,
    assignOffer,
    getAvailableOffersForAssignment,
    getCustomerDetails,
    getCustomerLoyaltyHistory,
    getCustomersDetailsForOffers,
    updateWallet,
    loading,
    error,
  };
};

